import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, LockOutlined, WorkspacePremiumOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../Store/store";
import { setToggleAuthModal } from "../../Redux/authReducer";
import APP_ROUTES from "../../Configurations/APP_ROUTES";

/**
 * The ONE upgrade prompt for the free/premium layer (October 2026, Task 10):
 * lock icon + headline + one line + CTA. Used three ways —
 *   <PremiumGate locked>…</PremiumGate>  blurs its children behind the prompt
 *   <UpgradeDialog open>                 the same prompt in a dialog, for
 *                                         click-to-reveal teasers
 *   <UpgradePrompt>                       the bare card, inline
 *
 * The CTA signs a logged-out visitor up; a signed-in free owner goes to the
 * Premium page.
 */

export const PREMIUM_PRICE_LABEL = "$50/year";

type PromptCopy = {
  title: string;
  description?: string;
  ctaLabel?: string;
};

const TEAL = "#004B4C";

export const UpgradePrompt: React.FC<
  PromptCopy & { compact?: boolean; onAction?: () => void }
> = ({ title, description, ctaLabel, compact, onAction }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((s: RootState) => s?.auth?.isAuthenticated);

  const handleClick = () => {
    onAction?.();
    if (!isAuthenticated) {
      dispatch(setToggleAuthModal(true));
      return;
    }
    navigate(String(APP_ROUTES.PREMIUM.pathname));
  };

  return (
    <Stack
      alignItems="center"
      textAlign="center"
      spacing={compact ? 1.5 : 2}
      sx={{
        bgcolor: "#fff",
        borderRadius: 3,
        border: "1px solid rgba(0,75,76,0.14)",
        boxShadow: "0 12px 32px rgba(0,40,41,0.14)",
        px: compact ? 4 : 6,
        py: compact ? 4 : 6,
        maxWidth: 380,
        mx: "auto",
      }}
      data-testid="upgrade-prompt"
    >
      <Box
        sx={{
          width: compact ? 40 : 48,
          height: compact ? 40 : 48,
          borderRadius: "50%",
          bgcolor: "rgba(0,75,76,0.08)",
          color: TEAL,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LockOutlined sx={{ fontSize: compact ? 20 : 24 }} />
      </Box>
      <Typography sx={{ fontWeight: 700, color: TEAL, fontSize: compact ? 16 : 18, lineHeight: 1.3 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={handleClick}
        startIcon={<WorkspacePremiumOutlined />}
        sx={{
          bgcolor: TEAL,
          "&:hover": { bgcolor: "#003637" },
          borderRadius: 2,
          px: 4,
          fontWeight: 700,
        }}
        data-testid="upgrade-cta"
      >
        {ctaLabel || (isAuthenticated ? "Upgrade to Premium" : "Sign up free")}
      </Button>
      {isAuthenticated && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Premium is {PREMIUM_PRICE_LABEL}
        </Typography>
      )}
    </Stack>
  );
};

type PremiumGateProps = PromptCopy & {
  locked: boolean;
  children: React.ReactNode;
  compact?: boolean;
  /** Keep the blurred preview at least this tall so the prompt fits. */
  minHeight?: number;
  testId?: string;
};

/** Blur `children` behind the upgrade prompt when `locked`; otherwise render them as-is. */
export const PremiumGate: React.FC<PremiumGateProps> = ({
  locked,
  children,
  compact,
  minHeight,
  testId,
  ...copy
}) => {
  const hiddenRef = useRef<HTMLDivElement>(null);

  // `inert` keeps the blurred controls out of the tab order and away from
  // screen readers. React 18's typings don't know the attribute yet.
  useEffect(() => {
    const el = hiddenRef.current;
    if (!el) return;
    if (locked) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }, [locked]);

  if (!locked) return <>{children}</>;

  return (
    <Box position="relative" sx={{ minHeight }} data-testid={testId || "premium-gate"}>
      <Box
        ref={hiddenRef}
        aria-hidden
        sx={{
          filter: "blur(6px)",
          pointerEvents: "none",
          userSelect: "none",
          maxHeight: compact ? 320 : 520,
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.8) 100%)",
        }}
      >
        <UpgradePrompt {...copy} compact={compact} />
      </Box>
    </Box>
  );
};

/** The same prompt in a dialog — for clickable blurred teasers. */
export const UpgradeDialog: React.FC<
  PromptCopy & { open: boolean; onClose: () => void }
> = ({ open, onClose, ...copy }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, bgcolor: "transparent", boxShadow: "none" } }}
  >
    <DialogContent sx={{ p: 0, position: "relative" }}>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
      >
        <Close fontSize="small" />
      </IconButton>
      <UpgradePrompt {...copy} onAction={onClose} />
    </DialogContent>
  </Dialog>
);

export default PremiumGate;
